import { Box, CircularProgress, IconButton, Stack, Typography, Tooltip } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useNotifications } from '@/hooks/use-notifications';
import Widget from '@/components/common/widget.tsx';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  type ChangeEvent,
  type MouseEvent,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react';
import type { MediaDto } from '@/types/dtos/media/media.dto.ts';
import { useDeleteMediaMutation } from '@/state/mutations/media/use-delete-media.ts';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import { VisuallyHiddenInput } from '@/components/common/visually-hidden.input.tsx';
import { useMediaUpload } from '@/hooks/use-media-upload.ts';
import { useMediaUploadQuotaModals } from '@/hooks/use-media-upload-quota-modals.ts';
import MediaUploadFeedback from '@/components/media/media-upload-feedback.tsx';
import InsufficientStorageContactModal from '@/components/events/detail/tabs/common/media/insufficient-storage-contact.modal.tsx';
import { useGetMediaDownloadUrlMutation } from '@/state/queries/media/use-get-media-download-url.ts';
import { downloadFile } from '@/lib/download-file.ts';
import { useDeleteMediaAssetMutation } from '@/state/mutations/media/use-delete-media-asset.ts';
import type { ServerError } from '@/types/errors/server.error.ts';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import { useApproveMediaAssetMutation } from '@/state/mutations/media/use-approve-media-asset.ts';
import { useEventStore } from '@/state/stores/use-event-store.ts';
import { useProductsStore } from '@/state/stores/use-products-store.ts';
import { PRODUCT_KEYS } from '@/types/dtos/orders/response/product.dto.ts';
import AddMediaStorageModal from '@/components/events/detail/tabs/common/media/add-media-storage.modal.tsx';
import { gigabytesToBytes } from '@/helpers/file.helper.ts';
import { getOriginEventId } from '@/helpers/event.helper';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { getQuotaKeyForProduct } from '@/helpers/product-quota.helper';
import { getMediaUploadErrorCode } from '@/helpers/media-upload-quota.helper.ts';
import { MediaAssetStatusFileIcon } from '@/components/events/detail/tabs/common/media/media-asset-status-file-icon';
import { useMediaPermissions } from '@/hooks/use-media-permissions';
import { MediaAssetApprovalControl } from '@/components/media/media-asset-approval-control.tsx';
import { MediaProcessingInsightBadge } from '@/components/media/media-processing-insight-badge.tsx';
import { MediaIncompleteUploadNotice } from '@/components/media/media-incomplete-upload-notice.tsx';
import {
  getProcessingInsightForAsset,
  isAssetApprovable,
  isAssetAutoApprovable,
  isAssetPreviewable,
} from '@/helpers/media-processing-insight.helper';
import type { MediaProcessingInstructionsContext } from '@/types/media-processing-instructions.types';

export interface ManageSessionMediaFormRef {
  triggerFileUpload: () => void;
}

interface ManageSessionMediaFormProps {
  media: MediaDto;
  onPreviewAsset?: (assetVersion: number) => void;
  getInstructionsContext?: (asset: MediaDto['assets'][number]) => MediaProcessingInstructionsContext | undefined;
  onUploadBusyChange?: (busy: boolean) => void;
  onUploadSettled?: (result: 'success' | 'error') => void;
}

const ManageSessionMediaForm = forwardRef<ManageSessionMediaFormRef, ManageSessionMediaFormProps>(
  ({ media, onPreviewAsset, getInstructionsContext, onUploadBusyChange, onUploadSettled }, ref) => {
    const t = useTranslations();
    const notification = useNotifications();
    const storeEvent = useEventStore((s) => s.event);
    const originId = getOriginEventId(storeEvent);
    const { data: quotaBalances } = useQuotaBalances('Event', originId);
    const catalogProducts = useProductsStore((s) => s.products);
    const permissions = useMediaPermissions(media);

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [mediaAssetVersionToDelete, setMediaAssetVersionToDelete] = useState<number | null>(null);
    const [approvalOverrides, setApprovalOverrides] = useState<Record<number, boolean>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      setApprovalOverrides((prev) => {
        if (Object.keys(prev).length === 0) return prev;
        const next = { ...prev };
        let changed = false;
        for (const [versionKey, approved] of Object.entries(prev)) {
          const version = Number(versionKey);
          const asset = media.assets.find((item) => item.version === version);
          if (asset && (asset.isApproved ?? false) === approved) {
            delete next[version];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, [media.assets, media.id]);

    const { mutateAsync: deleteMediaMutation } = useDeleteMediaMutation();
    const mediaUpload = useMediaUpload();
    const quotaModals = useMediaUploadQuotaModals(originId);
    const { mutateAsync: downloadMediaMutation, isPending: isDownloadPending } = useGetMediaDownloadUrlMutation();
    const { mutateAsync: approveMediaAssetMutation } = useApproveMediaAssetMutation();
    const { mutateAsync: deleteMediaAssetMutation, isPending: isAssetDeletingPending } = useDeleteMediaAssetMutation();

    useEffect(() => {
      onUploadBusyChange?.(mediaUpload.isBusy);
    }, [mediaUpload.isBusy, onUploadBusyChange]);

    // Compute mediaStorageSpace from features instead of using effect
    const mediaStorageSpace = useMemo(() => {
      if (!catalogProducts) return 0;
      const dataTransferProduct = catalogProducts.find((feature) => feature.key === PRODUCT_KEYS.DATA_TRANSFER_GB);
      const quotaKey = dataTransferProduct ? getQuotaKeyForProduct(dataTransferProduct) : undefined;
      if (!quotaKey) return 0;
      const row = quotaBalances?.find((b) => b.quotaKey === quotaKey);
      return row?.effectiveAvailable ?? row?.available ?? 0;
    }, [catalogProducts, quotaBalances]);

    const handleDeleteModalCancel = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setIsDeleteDialogOpen(false);
    };

    const handleDeleteMedia = async (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      try {
        await deleteMediaMutation({ mediaId: media.id });
        setIsDeleteDialogOpen(false);
        notification.notifySuccess(t('media.notification.deleteSuccess', { title: media.title }));
      } catch {
        notification.notifyError(t('media.notification.deleteError'));
      }
    };

    const handleUploadButtonClick = () => {
      if (mediaStorageSpace <= 0) {
        quotaModals.handleUploadError({ errorCode: 'INSUFFICIENT_STORAGE_SPACE' } as ServerError);
        return;
      }
      fileInputRef.current?.click();
    };

    useImperativeHandle(ref, () => ({
      triggerFileUpload: handleUploadButtonClick,
    }));

    const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files ? Array.from(event.target.files) : [];
      event.target.value = '';
      if (files.length === 0) {
        return;
      }

      const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
      if (totalBytes > gigabytesToBytes(mediaStorageSpace)) {
        quotaModals.handleUploadError({ errorCode: 'INSUFFICIENT_STORAGE_SPACE' } as ServerError);
        return;
      }

      try {
        const res = await mediaUpload.uploadFiles(media.id, files);

        if (isAssetAutoApprovable(res)) {
          try {
            await approveMediaAssetMutation({
              mediaId: media.id,
              assetVersion: res.version,
              isApproved: true,
              parentId: media.parent?.id,
              parentType: media.parentType,
            });
          } catch (approvalError) {
            console.error('Failed to auto-approve asset:', approvalError);
          }
        }

        notification.notifySuccess(t('media.notification.uploadSuccess', { name: res.name }));
        onUploadSettled?.('success');
        mediaUpload.reset();
      } catch (error) {
        if (!quotaModals.handleUploadError(error)) {
          const errorCode = getMediaUploadErrorCode(error);
          const errorMessage = t('media.notification.uploadError');
          const reason = errorCode ? `: ${t(`error.${errorCode}` as TranslationKey)}` : '';
          notification.notifyError(`${errorMessage}${reason}`);
        }
        onUploadSettled?.('error');
        mediaUpload.resetAfterUploadAttempt(error);
      }
    };

    const resolveAssetIsApproved = (asset: MediaDto['assets'][number]) =>
      approvalOverrides[asset.version] ?? asset.isApproved ?? false;

    const handleAssetApproval = async (assetVersion: number, currentApprovedState: boolean) => {
      const asset = media.assets.find((item) => item.version === assetVersion);
      const nextApproved = !currentApprovedState;
      if (nextApproved && asset && !isAssetApprovable(asset)) {
        return;
      }

      setApprovalOverrides((prev) => ({ ...prev, [assetVersion]: nextApproved }));
      try {
        await approveMediaAssetMutation({
          mediaId: media.id,
          assetVersion: assetVersion,
          isApproved: nextApproved,
          parentId: media.parent?.id,
          parentType: media.parentType,
        });

        notification.notifySuccess(
          t(
            !currentApprovedState
              ? 'media.notification.approveAssetSuccess'
              : 'media.notification.unapproveAssetSuccess',
            {
              version: assetVersion.toString(),
              title: media.title,
            },
          ),
        );
      } catch {
        setApprovalOverrides((prev) => {
          const next = { ...prev };
          delete next[assetVersion];
          return next;
        });
        notification.notifyError(
          t(!currentApprovedState ? 'media.notification.approveAssetError' : 'media.notification.unapproveAssetError', {
            version: assetVersion.toString(),
            title: media.title,
          }),
        );
      }
    };

    const uploadTooltipSlotProps = {
      tooltip: {
        sx: {
          maxWidth: '300px',
          fontSize: '0.875rem',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        },
      },
    } as const;

    const getUploadInfoForAsset = (asset: MediaDto['assets'][0]) => {
      const date = new Date(asset.createdAt);
      return t('media.uploadInfo', {
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString(),
        name: `${asset.createdBy.firstName} ${asset.createdBy.lastName}`,
      });
    };

    const handleFileDownload = async (event: MouseEvent<HTMLButtonElement>, asset: MediaDto['assets'][0]) => {
      event.stopPropagation();
      try {
        const { url } = await downloadMediaMutation(asset.uri);
        downloadFile(url);
      } catch {
        notification.notifyError(t('media.notification.downloadError'));
      }
    };

    const handleDeleteAsset = async () => {
      if (!mediaAssetVersionToDelete) return;

      try {
        await deleteMediaAssetMutation({ mediaId: media.id, assetVersion: mediaAssetVersionToDelete });
        setMediaAssetVersionToDelete(null);

        notification.notifySuccess(
          t('media.notification.deleteAssetSuccess', {
            version: mediaAssetVersionToDelete.toString(),
            title: media.title,
          }),
        );
      } catch {
        notification.notifyError(t('media.notification.deleteAssetError'));
      }
    };

    return (
      <>
        <ConfirmationModal
          open={isDeleteDialogOpen}
          title={t('media.deleteMediaModalTitle')}
          text={t('media.deleteMediaModalText', { title: media.title })}
          onConfirm={handleDeleteMedia}
          onCancel={handleDeleteModalCancel}
        />
        <ConfirmationModal
          open={!!mediaAssetVersionToDelete}
          title={t('media.deleteAssetModalTitle')}
          text={t('media.deleteAssetModalText', {
            version: mediaAssetVersionToDelete?.toString() || '',
            title: media.title,
          })}
          onConfirm={handleDeleteAsset}
          onCancel={() => setMediaAssetVersionToDelete(null)}
        />
        <AddMediaStorageModal
          open={quotaModals.isAddStorageModalOpen}
          onModalClose={() => quotaModals.setIsAddStorageModalOpen(false)}
        />
        <InsufficientStorageContactModal
          open={quotaModals.isContactModalOpen}
          onModalClose={() => quotaModals.setIsContactModalOpen(false)}
        />

        <Widget>
          <VisuallyHiddenInput
            type="file"
            multiple
            onChange={handleFileUpload}
            ref={fileInputRef}
            disabled={mediaUpload.isBusy}
          />
          <MediaUploadFeedback mediaUpload={mediaUpload} />
          <MediaIncompleteUploadNotice media={media} hidden={mediaUpload.isBusy} />
          {media.assets.some((asset) => asset.uploadStatus !== 'uploading') && (
            <Stack spacing={0.5} mt={1}>
              {[...media.assets]
                .filter((asset) => asset.uploadStatus !== 'uploading')
                .reverse()
                .map((asset) => {
                  const assetPreviewable = isAssetPreviewable(asset);
                  return (
                    <Box
                      key={asset.version}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        py: 0.5,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        onClick={(e) => {
                          if (onPreviewAsset && assetPreviewable) {
                            e.stopPropagation();
                            onPreviewAsset(asset.version);
                          }
                        }}
                        sx={{
                          'display': 'inline-flex',
                          'cursor': onPreviewAsset && assetPreviewable ? 'pointer' : 'default',
                          'flex': 1,
                          'minWidth': 0,
                          '&:hover':
                            onPreviewAsset && assetPreviewable
                              ? {
                                  opacity: 0.7,
                                }
                              : {},
                        }}
                      >
                        {asset.fileType && <MediaAssetStatusFileIcon asset={asset} />}
                        <Stack spacing={0}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Tooltip
                              title={getUploadInfoForAsset(asset)}
                              placement="top-start"
                              disableInteractive
                              slotProps={uploadTooltipSlotProps}
                            >
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{ display: 'inline-block', width: 'fit-content' }}
                              >
                                {t('common.version')} {asset.version}
                              </Typography>
                            </Tooltip>
                            <MediaProcessingInsightBadge
                              insight={getProcessingInsightForAsset(asset)}
                              instructionsContext={getInstructionsContext?.(asset)}
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(asset.createdAt).toLocaleDateString()},{' '}
                            {new Date(asset.createdAt).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack direction="row" spacing={0} alignItems="center">
                        <MediaAssetApprovalControl
                          isApproved={resolveAssetIsApproved(asset)}
                          canApprove={permissions.canApprove}
                          isApprovable={isAssetApprovable(asset)}
                          onToggle={() => handleAssetApproval(asset.version, resolveAssetIsApproved(asset))}
                        />

                        {/* Download Icon - Only show if user has download_asset permission */}
                        {permissions.canDownload && asset.uploadStatus !== 'uploading' && (
                          <Tooltip title={t('media.download' as TranslationKey)} placement="left" disableInteractive>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: 34,
                                height: 34,
                                justifyContent: 'center',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => handleFileDownload(e, asset)}
                                disabled={isDownloadPending}
                                sx={{
                                  '& > svg': {
                                    fontSize: '1.5rem',
                                    width: '1.5rem',
                                    height: '1.5rem',
                                  },
                                }}
                              >
                                {isDownloadPending ? (
                                  <CircularProgress size={20} sx={{ width: '1.25rem', height: '1.25rem' }} />
                                ) : (
                                  <FileDownloadIcon />
                                )}
                              </IconButton>
                            </Box>
                          </Tooltip>
                        )}

                        {/* Delete Icon - Only show if user has delete_asset permission */}
                        {permissions.canDeleteAsset && (
                          <Tooltip title={t('media.delete' as TranslationKey)} placement="left" disableInteractive>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: 34,
                                height: 34,
                                justifyContent: 'center',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMediaAssetVersionToDelete(asset.version);
                                }}
                                disabled={isAssetDeletingPending}
                                sx={{
                                  '& > svg': {
                                    fontSize: '1.5rem',
                                    width: '1.5rem',
                                    height: '1.5rem',
                                  },
                                  'color': 'action.active',
                                }}
                              >
                                {isAssetDeletingPending ? (
                                  <CircularProgress size={20} sx={{ width: '1.25rem', height: '1.25rem' }} />
                                ) : (
                                  <DeleteIcon />
                                )}
                              </IconButton>
                            </Box>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
            </Stack>
          )}
        </Widget>
      </>
    );
  },
);

ManageSessionMediaForm.displayName = 'ManageSessionMediaForm';

export default ManageSessionMediaForm;
